import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  CodeBracketIcon,
  DevicePhoneMobileIcon,
  CloudArrowUpIcon,
  CpuChipIcon,
  PaintBrushIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Capabilities: React.FC = () => {
  const coreCapabilities = [
    {
      icon: CodeBracketIcon,
      title: 'Custom Software Development',
      description: 'End-to-end custom software solutions tailored to your business needs.',
      capabilities: [
        'Full-stack web application development',
        'Enterprise software solutions',
        'Legacy system modernization',
        'API development and integration',
        'Microservices architecture'
      ]
    },
    {
      icon: DevicePhoneMobileIcon,
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications for iOS and Android.',
      capabilities: [
        'iOS and Android native apps',
        'React Native cross-platform apps',
        'Progressive Web Apps (PWA)',
        'Mobile UI/UX design',
        'App store deployment and maintenance'
      ]
    },
    {
      icon: CpuChipIcon,
      title: 'AI & Machine Learning',
      description: 'Intelligent solutions powered by artificial intelligence and machine learning.',
      capabilities: [
        'AI-powered chatbots and assistants',
        'Predictive analytics and forecasting',
        'Natural Language Processing (NLP)',
        'Computer vision solutions',
        'Machine learning model development'
      ]
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and migration services.',
      capabilities: [
        'AWS, Azure, Google Cloud deployment',
        'Cloud migration and optimization',
        'Serverless architecture',
        'Cloud security and compliance',
        'DevOps and CI/CD pipelines'
      ]
    },
    {
      icon: PaintBrushIcon,
      title: 'UI/UX Design',
      description: 'User-centered design that delivers exceptional digital experiences.',
      capabilities: [
        'User research and persona development',
        'Wireframing and prototyping',
        'Visual design and branding',
        'Responsive web design',
        'Usability testing and optimization'
      ]
    },
    {
      icon: ChartBarIcon,
      title: 'Data Analytics & BI',
      description: 'Transform data into actionable business insights.',
      capabilities: [
        'Business intelligence dashboards',
        'Data visualization and reporting',
        'ETL and data pipeline development',
        'Big data processing',
        'Real-time analytics solutions'
      ]
    }
  ];

  const technicalCapabilities = [
    {
      category: 'Frontend Technologies',
      items: ['React.js', 'Vue.js', 'Angular', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Material-UI']
    },
    {
      category: 'Backend Technologies',
      items: ['Node.js', 'Python', 'Java', '.NET', 'PHP', 'Ruby on Rails', 'Go']
    },
    {
      category: 'Mobile Development',
      items: ['React Native', 'Swift', 'Kotlin', 'Flutter', 'Ionic', 'Xamarin']
    },
    {
      category: 'Cloud & DevOps',
      items: ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Jenkins', 'GitLab CI/CD']
    },
    {
      category: 'Databases',
      items: ['PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'DynamoDB', 'Elasticsearch', 'Firebase']
    },
    {
      category: 'AI & ML',
      items: ['TensorFlow', 'PyTorch', 'OpenAI', 'Hugging Face', 'scikit-learn', 'Keras']
    }
  ];

  const industryExpertise = [
    { name: 'Healthcare & Medical', icon: '🏥' },
    { name: 'Financial Services', icon: '💰' },
    { name: 'E-commerce & Retail', icon: '🛒' },
    { name: 'Education & E-learning', icon: '📚' },
    { name: 'Real Estate & Property', icon: '🏠' },
    { name: 'Professional Services', icon: '💼' },
    { name: 'Entertainment & Media', icon: '🎬' },
    { name: 'Manufacturing & Logistics', icon: '🏭' }
  ];

  const deliveryCapabilities = [
    {
      icon: RocketLaunchIcon,
      title: 'Rapid Development',
      description: 'Agile methodology with 2-week sprints for faster time-to-market'
    },
    {
      icon: UserGroupIcon,
      title: 'Dedicated Teams',
      description: 'Skilled developers, designers, and project managers assigned to your project'
    },
    {
      icon: ClockIcon,
      title: '24/7 Support',
      description: 'Round-the-clock support and maintenance for your applications'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Quality Assurance',
      description: 'Comprehensive testing and QA processes to ensure flawless delivery'
    }
  ];

  const certifications = [
    'ISO 9001:2015 Certified',
    'AWS Certified Solutions Architect',
    'Microsoft Certified: Azure Solutions Architect',
    'Google Cloud Professional Architect',
    'Certified Scrum Master (CSM)',
    'PMP Certified Project Managers'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-secondary-900 via-primary-800 to-secondary-800 text-white">
        <div className="container-custom section-padding">
          <div className="text-center max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Our Capabilities
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-primary-100 mb-8 leading-relaxed"
            >
              Comprehensive software development and digital solutions backed by 20+ years of experience. 
              We deliver excellence across every technology stack and industry vertical.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Link to="/contact" className="btn-primary bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white">
                Request Capability Statement
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Core Service Capabilities
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Our comprehensive suite of services covers every aspect of modern software development.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreCapabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-secondary-200"
              >
                <capability.icon className="w-12 h-12 text-primary-600 mb-4" />
                <h3 className="text-xl font-bold text-secondary-900 mb-3">
                  {capability.title}
                </h3>
                <p className="text-secondary-600 mb-4">
                  {capability.description}
                </p>
                <ul className="space-y-2">
                  {capability.capabilities.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <CheckCircleIcon className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" />
                      <span className="text-secondary-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Capabilities */}
      <section className="section-padding bg-secondary-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Technical Expertise
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Proficient in the latest technologies and frameworks across the full development stack.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {technicalCapabilities.map((tech, index) => (
              <motion.div
                key={tech.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md"
              >
                <h3 className="text-lg font-bold text-secondary-900 mb-4 pb-3 border-b-2 border-primary-500">
                  {tech.category}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tech.items.map((item, idx) => (
                    <span key={idx} className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Expertise */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Industry Expertise
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Deep domain knowledge across multiple industries with proven success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industryExpertise.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-gradient-to-br from-primary-500 to-accent-600 text-white rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-4xl mb-3">{industry.icon}</div>
                <h3 className="font-semibold text-lg">{industry.name}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Capabilities */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Delivery Excellence
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Our proven delivery methodology ensures successful project outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {deliveryCapabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <capability.icon className="w-10 h-10 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-secondary-900 mb-2">
                  {capability.title}
                </h3>
                <p className="text-secondary-600 text-sm">
                  {capability.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Standards */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Certifications & Standards
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Industry-recognized certifications ensuring quality and compliance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-md border-l-4 border-accent-500"
              >
                <ShieldCheckIcon className="w-8 h-8 text-accent-600 flex-shrink-0" />
                <span className="text-secondary-900 font-medium">{cert}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Leverage Our Capabilities?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Download our comprehensive capability statement or get in touch to discuss your project requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white">
              Get In Touch
            </Link>
            <Link to="/work" className="btn-secondary bg-transparent border-2 border-white text-cyan-300 hover:bg-cyan-500 hover:text-white">
              View Our Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Capabilities;

